import { z } from 'zod';
import {
  normalizeOptionalBuildTemplateId,
  normalizeOptionalIdentityTemplateId,
} from '@/IsekaiRPG/modules/character/characterSemantic';

import { CharacterSkillSchema, StatusEffectSchema } from '@/IsekaiRPG/modules/skill/skillTypes';
import {
  AppearanceSchema,
  CoreTraitsSchema,
  FormativeEventSchema,
  GenderSchema,
} from './characterProfile';
import {
  CharacterPresenceCommitmentSchema,
  CharacterPresenceConstraintsSchema,
  CharacterPresenceMobilitySchema,
  CharacterPresencePlaceSchema,
  CharacterPresencePreferencesSchema,
  CharacterPresenceRelationshipSchema,
  CharacterPresenceSceneSchema,
  CharacterZoneAnchorRefSchema,
} from './characterPresence';
import { AttributesSchema, EquipmentSlotsSchema } from './characterEntity';

export const CharacterProfilePatchSchema = z.object({
  name: z.string().optional(),
  gender: GenderSchema.optional(),
  age: z.string().optional(),
  avatar: z.string().optional(),
  country: z.string().optional(),
  identity: z.string().optional(),
  background: z.string().optional(),
  combat_style: z.string().optional(),
  appearance: AppearanceSchema.partial().optional(),
  coreTraits: CoreTraitsSchema.optional(),
  addTags: z.array(z.string()).default([]),
  removeTags: z.array(z.string()).default([]),
  appendFormativeEvents: z.array(FormativeEventSchema).default([]),
  removeFormativeEventEvents: z.array(z.string()).default([]),
  appendLikes: z.array(z.string()).default([]),
  removeLikes: z.array(z.string()).default([]),
  appendDislikes: z.array(z.string()).default([]),
  removeDislikes: z.array(z.string()).default([]),
});
export type CharacterProfilePatch = z.infer<typeof CharacterProfilePatchSchema>;

export const CharacterEntityPatchSchema = z.object({
  identityTemplateId: z
    .preprocess(
      value => normalizeOptionalIdentityTemplateId(typeof value === 'string' ? value : undefined),
      z.string().optional(),
    )
    .optional(),
  buildTemplateId: z
    .preprocess(
      value => normalizeOptionalBuildTemplateId(typeof value === 'string' ? value : undefined),
      z.string().optional(),
    )
    .optional(),
  attributes: AttributesSchema.partial().optional(),
  hp: z.number().min(0).optional(),
  maxHp: z.number().min(1).optional(),
  isAlive: z.boolean().optional(),
  statusEffects: z.array(StatusEffectSchema).optional(),
  equipment: EquipmentSlotsSchema.partial().optional(),
  skillsUpsert: z.record(z.string(), CharacterSkillSchema).default({}),
  skillsRemove: z.array(z.string()).default([]),
});
export type CharacterEntityPatch = z.infer<typeof CharacterEntityPatchSchema>;

export const CharacterPresencePatchSchema = z.object({
  home: CharacterZoneAnchorRefSchema.partial().optional(),
  activityCenter: CharacterZoneAnchorRefSchema.partial().optional(),
  mobility: CharacterPresenceMobilitySchema.partial().optional(),
  places: z.array(CharacterPresencePlaceSchema).optional(),
  commitments: z.array(CharacterPresenceCommitmentSchema).optional(),
  preferences: CharacterPresencePreferencesSchema.partial().optional(),
  constraints: CharacterPresenceConstraintsSchema.partial().optional(),
  relationships: z.array(CharacterPresenceRelationshipSchema).optional(),
  scene: CharacterPresenceSceneSchema.partial().optional(),
});
export type CharacterPresencePatch = z.infer<typeof CharacterPresencePatchSchema>;

export const CharacterPatchSchema = z.object({
  profilePatch: CharacterProfilePatchSchema.default(() => CharacterProfilePatchSchema.parse({})),
  entityPatch: CharacterEntityPatchSchema.default(() => CharacterEntityPatchSchema.parse({})),
  presencePatch: CharacterPresencePatchSchema.default(() => CharacterPresencePatchSchema.parse({})),
});
export type CharacterPatch = z.input<typeof CharacterPatchSchema>;
export type ResolvedCharacterPatch = z.output<typeof CharacterPatchSchema>;
